/**
 * Submit handler for the "update workers request" path.
 *
 * Triggered when an existing `workersRequest` is being edited. Depending on the
 * action capabilities (`canBeUpdated`, `requestedWorkersCanBeUpdated`) it may:
 * - update the mission-level data (workPeriods, service, address),
 * - update existing posts whose values actually changed (diff vs initial values),
 * - create new draft posts that were added inline.
 *
 * The post-diff is computed here from `initialValues` + `values`, replacing the
 * anti-pattern where `isPostValuesUpdated` was mutated during render.
 */
import { isEqual } from 'lodash'
import { WorkersRequest } from '../../../../../api/workers-requests'
import {
  useCreatePost,
  useUpdateMutation,
  useUpdatePost,
} from '../../../../../queries/workers-requests'
import {
  WorkersRequestCreatePostValues,
  WorkersRequestUpdateFormValues,
} from '../workers-request-form.types'
import { buildWorkPeriodsPayload } from './build-work-periods-payload'

/**
 * Dependencies required by the update-submit handler.
 */
export interface SubmitUpdateDeps {
  workersRequest: WorkersRequest
  updateWorkersRequestMutation: ReturnType<typeof useUpdateMutation>
  updatePostMutation: ReturnType<typeof useUpdatePost>
  createPostMutation: ReturnType<typeof useCreatePost>
}

/**
 * Type-guard: narrows a post value to "existing post" (already persisted).
 */
const isExistingPost = <T extends object>(post: T): post is T & { _id: string } =>
  '_id' in post && Boolean((post as { _id?: unknown })._id)

/**
 * Submits an update to an existing workers request.
 *
 * @param values - Current Formik values.
 * @param initialValues - Pristine Formik values at mount time (used for post-diff).
 * @param deps - Mutation hooks and the resource being edited.
 */
export const submitUpdate = (
  values: WorkersRequestUpdateFormValues,
  initialValues: WorkersRequestUpdateFormValues,
  deps: SubmitUpdateDeps,
): void => {
  const { workersRequest } = deps
  const workPeriods = buildWorkPeriodsPayload(values)

  // 1. Update mission-level data if allowed.
  if (workersRequest.__actions.canBeUpdated) {
    const { missionData } = values
    deps.updateWorkersRequestMutation.mutate({
      id: workersRequest._id,
      missionData: {
        workPeriods,
        service: missionData.service,
        address: missionData.address,
      },
    })
  }

  // Early exit if posts cannot be updated.
  if (!workersRequest.__actions.requestedWorkersCanBeUpdated) return

  // 2. Update existing posts whose values changed.
  // Diff is computed from the initial vs current Formik values (no render-time mutation).
  for (const post of values.posts) {
    if (!isExistingPost(post)) continue
    const initialPost = initialValues.posts.find(p => isExistingPost(p) && p._id === post._id)
    if (!initialPost) continue
    if (isEqual(initialPost, post)) continue

    const { desiredWorkers, recourseCase, jobTitle, description, tags } = post
    deps.updatePostMutation.mutate({
      workersRequestId: workersRequest._id.toString(),
      postId: post._id,
      desiredWorkers,
      recourseCase,
      jobTitleId: jobTitle,
      description,
      tags: tags?.map(tag => tag._id) || [],
    })
  }

  // 3. Create freshly-added draft posts. Each draft is expanded into `count` payloads.
  const draftPosts = values.posts.filter(post => !isExistingPost(post))
  const postsPayload = draftPosts.reduce(
    (all, current) => {
      const { count, ...post } = current
      for (let i = 0; i < count; i++) {
        all.push(post)
      }
      return all
    },
    [] as Array<Omit<WorkersRequestCreatePostValues, 'count'>>,
  )

  for (const post of postsPayload) {
    const { desiredWorkers, recourseCase, jobTitle, description, tags } = post
    deps.createPostMutation.mutate({
      workersRequestId: workersRequest._id.toString(),
      desiredWorkers,
      recourseCase,
      jobTitle,
      description,
      tags: tags?.map(tag => tag._id) || [],
    })
  }
}
